/**
 * Passcode Lock Context
 * Provides local-only passcode lock system with SHA-256 hashing
 * Supports both numeric PINs and alphanumeric passwords
 * No external services required - works entirely in localStorage
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { wipeAllData as wipeDatabase } from '../lib/localDb';

// Storage keys
const STORAGE_KEYS = {
  SALT: 'armsroom.pin.v1.salt',
  HASH: 'armsroom.pin.v1.hash',
  LAST_UNLOCKED: 'armsroom.pin.v1.lastUnlockedAt',
  FAILED_ATTEMPTS: 'armsroom.pin.v1.failedAttempts',
  COOLDOWN_UNTIL: 'armsroom.pin.v1.cooldownUntil',
  BACKGROUNDED_AT: 'armsroom.pin.v1.backgroundedAt',
  PASSCODE_TYPE: 'armsroom.pin.v1.passcodeType', // 'pin' or 'password'
};

// Lock timeout in milliseconds (1 minute)
const LOCK_TIMEOUT_MS = 1 * 60 * 1000;

// Idle lock: lock if the app stays open in the foreground with no interaction
const IDLE_LOCK_MS = 5 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 15 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel'];

const PinContext = createContext({
  isUnlocked: false,
  isPinSet: false,
  isLoading: true,
  failedAttempts: 0,
  cooldownUntil: null,
  passcodeType: 'pin', // 'pin' or 'password'
  unlock: () => Promise.resolve(false),
  setupPin: () => Promise.resolve(false),
  setupPasscode: () => Promise.resolve(false),
  changePin: () => Promise.resolve(false),
  changePasscode: () => Promise.resolve(false),
  lock: () => {},
  resetPin: () => Promise.resolve(),
  wipeAllAppData: () => Promise.resolve(),
  clearCooldown: () => {},
});

export const usePin = () => {
  const context = useContext(PinContext);
  if (!context) {
    throw new Error('usePin must be used within a PinProvider');
  }
  return context;
};

/**
 * Generate a random salt for PIN hashing
 */
const generateSalt = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Hash passcode with salt using SHA-256
 * Works for both PINs and passwords
 */
const hashPin = async (passcode, salt) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(passcode + salt);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
};

export const PinProvider = ({ children }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isPinSet, setIsPinSet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownUntil, setCooldownUntil] = useState(null);
  const [passcodeType, setPasscodeType] = useState('pin'); // 'pin' or 'password'
  const backgroundedAtRef = useRef(null);

  // Check if passcode is set on mount and load brute-force state
  useEffect(() => {
    const checkPinStatus = () => {
      const salt = localStorage.getItem(STORAGE_KEYS.SALT);
      const hash = localStorage.getItem(STORAGE_KEYS.HASH);
      setIsPinSet(!!(salt && hash));
      
      // Load passcode type (default to 'pin' for backward compatibility)
      const storedType = localStorage.getItem(STORAGE_KEYS.PASSCODE_TYPE);
      setPasscodeType(storedType || 'pin');
      
      // Check if user is already unlocked
      const lastUnlocked = localStorage.getItem(STORAGE_KEYS.LAST_UNLOCKED);
      if (lastUnlocked && salt && hash) {
        const lastUnlockedTime = parseInt(lastUnlocked, 10);
        const timeSinceUnlock = Date.now() - lastUnlockedTime;
        
        // If unlocked recently (within timeout), restore unlocked state
        if (timeSinceUnlock < LOCK_TIMEOUT_MS) {
          setIsUnlocked(true);
        } else {
          // Timeout expired, clear the unlock timestamp
          localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
          setIsUnlocked(false);
        }
      } else {
        setIsUnlocked(false);
      }
      
      // Load failed attempts and cooldown from storage
      const storedAttempts = parseInt(localStorage.getItem(STORAGE_KEYS.FAILED_ATTEMPTS) || '0', 10);
      const storedCooldown = parseInt(localStorage.getItem(STORAGE_KEYS.COOLDOWN_UNTIL) || '0', 10);
      
      setFailedAttempts(storedAttempts);
      if (storedCooldown > Date.now()) {
        setCooldownUntil(storedCooldown);
      } else {
        // Clear expired cooldown
        localStorage.removeItem(STORAGE_KEYS.COOLDOWN_UNTIL);
        setCooldownUntil(null);
      }
      
      setIsLoading(false);
    };

    checkPinStatus();
  }, []);

  // Handle visibility change (tab background/foreground)
  // Uses localStorage to persist backgrounded timestamp across page refreshes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // App going to background - record timestamp in localStorage for persistence
        const now = Date.now();
        localStorage.setItem(STORAGE_KEYS.BACKGROUNDED_AT, now.toString());
        backgroundedAtRef.current = now;
        console.log('[PIN] App backgrounded at', new Date(now).toLocaleTimeString());
      } else {
        // App coming to foreground - check if we should lock
        // Read from localStorage in case the ref was lost (e.g., hot reload)
        const storedBackgroundedAt = localStorage.getItem(STORAGE_KEYS.BACKGROUNDED_AT);
        const backgroundedAt = storedBackgroundedAt ? parseInt(storedBackgroundedAt, 10) : backgroundedAtRef.current;
        
        if (backgroundedAt && isUnlocked) {
          const backgroundDuration = Date.now() - backgroundedAt;
          console.log('[PIN] App foregrounded, was backgrounded for', Math.round(backgroundDuration / 1000), 'seconds');
          
          if (backgroundDuration > LOCK_TIMEOUT_MS) {
            console.log('[PIN] Locking app - backgrounded for', Math.round(backgroundDuration / 1000), 'seconds (threshold:', LOCK_TIMEOUT_MS / 1000, 's)');
            setIsUnlocked(false);
            localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
          }
        }
        
        // Clear the backgrounded timestamp
        localStorage.removeItem(STORAGE_KEYS.BACKGROUNDED_AT);
        backgroundedAtRef.current = null;
      }
    };

    // Also check on mount if we were backgrounded before (e.g., page was refreshed while backgrounded)
    const checkBackgroundedState = () => {
      const storedBackgroundedAt = localStorage.getItem(STORAGE_KEYS.BACKGROUNDED_AT);
      if (storedBackgroundedAt && isUnlocked) {
        const backgroundedAt = parseInt(storedBackgroundedAt, 10);
        const backgroundDuration = Date.now() - backgroundedAt;
        
        if (backgroundDuration > LOCK_TIMEOUT_MS) {
          console.log('[PIN] Locking app on mount - was backgrounded for', Math.round(backgroundDuration / 1000), 'seconds');
          setIsUnlocked(false);
          localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
          localStorage.removeItem(STORAGE_KEYS.BACKGROUNDED_AT);
        }
      }
    };
    
    checkBackgroundedState();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isUnlocked]);

  // Idle lock - covers the case where the app is left open on screen
  useEffect(() => {
    if (!isUnlocked) return undefined;

    let lastActivity = Date.now();
    const markActive = () => {
      lastActivity = Date.now();
    };

    const intervalId = setInterval(() => {
      if (Date.now() - lastActivity > IDLE_LOCK_MS) {
        console.log('[PIN] Locking app - idle for', Math.round((Date.now() - lastActivity) / 1000), 'seconds');
        setIsUnlocked(false);
        localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
      }
    }, IDLE_CHECK_INTERVAL_MS);

    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, markActive, { passive: true, capture: true }));
    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, markActive, { capture: true }));
    };
  }, [isUnlocked]);

  // Calculate cooldown duration based on failed attempts
  const getCooldownDuration = useCallback((attempts) => {
    if (attempts >= 15) return 5 * 60 * 1000; // 5 minutes
    if (attempts >= 10) return 60 * 1000; // 60 seconds
    if (attempts >= 5) return 30 * 1000; // 30 seconds
    return 0;
  }, []);

  // Unlock with PIN
  const unlock = useCallback(async (pin) => {
    try {
      // Check if in cooldown
      const storedCooldown = parseInt(localStorage.getItem(STORAGE_KEYS.COOLDOWN_UNTIL) || '0', 10);
      if (storedCooldown > Date.now()) {
        setCooldownUntil(storedCooldown);
        return { success: false, cooldown: true };
      }

      const salt = localStorage.getItem(STORAGE_KEYS.SALT);
      const storedHash = localStorage.getItem(STORAGE_KEYS.HASH);

      if (!salt || !storedHash) {
        return { success: false };
      }

      const inputHash = await hashPin(pin, salt);
      
      if (inputHash === storedHash) {
        // Success - reset attempts and cooldown
        setIsUnlocked(true);
        setFailedAttempts(0);
        setCooldownUntil(null);
        localStorage.setItem(STORAGE_KEYS.LAST_UNLOCKED, Date.now().toString());
        localStorage.removeItem(STORAGE_KEYS.FAILED_ATTEMPTS);
        localStorage.removeItem(STORAGE_KEYS.COOLDOWN_UNTIL);
        return { success: true };
      }

      // Failed attempt - increment counter and potentially set cooldown
      const currentAttempts = parseInt(localStorage.getItem(STORAGE_KEYS.FAILED_ATTEMPTS) || '0', 10);
      const newAttempts = currentAttempts + 1;
      localStorage.setItem(STORAGE_KEYS.FAILED_ATTEMPTS, newAttempts.toString());
      setFailedAttempts(newAttempts);

      // Check if cooldown should be applied
      const cooldownDuration = getCooldownDuration(newAttempts);
      if (cooldownDuration > 0) {
        const cooldownEnd = Date.now() + cooldownDuration;
        localStorage.setItem(STORAGE_KEYS.COOLDOWN_UNTIL, cooldownEnd.toString());
        setCooldownUntil(cooldownEnd);
        return { success: false, cooldown: true, cooldownEnd, attempts: newAttempts };
      }

      return { success: false, attempts: newAttempts };
    } catch (error) {
      console.error('[PIN] Unlock error:', error);
      return { success: false, error: true };
    }
  }, [getCooldownDuration]);

  // Clear cooldown (for UI updates)
  const clearCooldown = useCallback(() => {
    setCooldownUntil(null);
    localStorage.removeItem(STORAGE_KEYS.COOLDOWN_UNTIL);
  }, []);

  // Setup new PIN (numeric only, 4-8 digits) - kept for backward compatibility
  const setupPin = useCallback(async (pin) => {
    try {
      // Validate PIN length (4-8 digits)
      if (!/^\d{4,8}$/.test(pin)) {
        return false;
      }

      const salt = generateSalt();
      const hash = await hashPin(pin, salt);

      localStorage.setItem(STORAGE_KEYS.SALT, salt);
      localStorage.setItem(STORAGE_KEYS.HASH, hash);
      localStorage.setItem(STORAGE_KEYS.LAST_UNLOCKED, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.PASSCODE_TYPE, 'pin');

      setIsPinSet(true);
      setIsUnlocked(true);
      setPasscodeType('pin');

      console.log('[PIN] PIN set successfully');
      return true;
    } catch (error) {
      console.error('[PIN] Setup error:', error);
      return false;
    }
  }, []);

  // Setup new passcode (PIN or password)
  // type: 'pin' (numeric 4-8 digits) or 'password' (alphanumeric 6+ chars)
  const setupPasscode = useCallback(async (passcode, type = 'pin') => {
    try {
      // Validate based on type
      if (type === 'pin') {
        if (!/^\d{4,8}$/.test(passcode)) {
          return { success: false, error: 'PIN must be 4-8 digits' };
        }
      } else if (type === 'password') {
        if (passcode.length < 6) {
          return { success: false, error: 'Password must be at least 6 characters' };
        }
      } else {
        return { success: false, error: 'Invalid passcode type' };
      }

      const salt = generateSalt();
      const hash = await hashPin(passcode, salt);

      localStorage.setItem(STORAGE_KEYS.SALT, salt);
      localStorage.setItem(STORAGE_KEYS.HASH, hash);
      localStorage.setItem(STORAGE_KEYS.LAST_UNLOCKED, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.PASSCODE_TYPE, type);

      setIsPinSet(true);
      setIsUnlocked(true);
      setPasscodeType(type);

      console.log(`[PIN] ${type === 'pin' ? 'PIN' : 'Password'} set successfully`);
      return { success: true };
    } catch (error) {
      console.error('[PIN] Setup error:', error);
      return { success: false, error: 'Setup failed' };
    }
  }, []);

  // Change PIN (requires current passcode verification) - kept for backward compatibility
  const changePin = useCallback(async (currentPin, newPin) => {
    try {
      // Verify current passcode
      const salt = localStorage.getItem(STORAGE_KEYS.SALT);
      const storedHash = localStorage.getItem(STORAGE_KEYS.HASH);

      if (!salt || !storedHash) {
        return false;
      }

      const currentHash = await hashPin(currentPin, salt);
      if (currentHash !== storedHash) {
        return false;
      }

      // Validate new PIN length (4-8 digits)
      if (!/^\d{4,8}$/.test(newPin)) {
        return false;
      }

      // Set new PIN with new salt
      const newSalt = generateSalt();
      const newHash = await hashPin(newPin, newSalt);

      localStorage.setItem(STORAGE_KEYS.SALT, newSalt);
      localStorage.setItem(STORAGE_KEYS.HASH, newHash);
      localStorage.setItem(STORAGE_KEYS.LAST_UNLOCKED, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.PASSCODE_TYPE, 'pin');

      setPasscodeType('pin');

      console.log('[PIN] PIN changed successfully');
      return true;
    } catch (error) {
      console.error('[PIN] Change error:', error);
      return false;
    }
  }, []);

  // Change passcode (PIN or password)
  const changePasscode = useCallback(async (currentPasscode, newPasscode, newType = 'pin') => {
    try {
      // Verify current passcode
      const salt = localStorage.getItem(STORAGE_KEYS.SALT);
      const storedHash = localStorage.getItem(STORAGE_KEYS.HASH);

      if (!salt || !storedHash) {
        return { success: false, error: 'No passcode set' };
      }

      const currentHash = await hashPin(currentPasscode, salt);
      if (currentHash !== storedHash) {
        return { success: false, error: 'Current passcode is incorrect' };
      }

      // Validate new passcode based on type
      if (newType === 'pin') {
        if (!/^\d{4,8}$/.test(newPasscode)) {
          return { success: false, error: 'PIN must be 4-8 digits' };
        }
      } else if (newType === 'password') {
        if (newPasscode.length < 6) {
          return { success: false, error: 'Password must be at least 6 characters' };
        }
      } else {
        return { success: false, error: 'Invalid passcode type' };
      }

      // Set new passcode with new salt
      const newSalt = generateSalt();
      const newHash = await hashPin(newPasscode, newSalt);

      localStorage.setItem(STORAGE_KEYS.SALT, newSalt);
      localStorage.setItem(STORAGE_KEYS.HASH, newHash);
      localStorage.setItem(STORAGE_KEYS.LAST_UNLOCKED, Date.now().toString());
      localStorage.setItem(STORAGE_KEYS.PASSCODE_TYPE, newType);

      setPasscodeType(newType);

      console.log(`[PIN] ${newType === 'pin' ? 'PIN' : 'Password'} changed successfully`);
      return { success: true };
    } catch (error) {
      console.error('[PIN] Change error:', error);
      return { success: false, error: 'Change failed' };
    }
  }, []);

  // Lock the app manually
  const lock = useCallback(() => {
    setIsUnlocked(false);
    localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
    console.log('[PIN] App locked manually');
  }, []);

  /**
   * Wipe ALL app data - single source of truth for data wipe
   * Used by both "Forgot Passcode" and "Erase All Data"
   * This is an async function that properly awaits IndexedDB deletion
   */
  const wipeAllAppData = useCallback(async () => {
    console.log('[PIN] Starting full app data wipe...');
    
    // 1. Clear all passcode-related localStorage
    localStorage.removeItem(STORAGE_KEYS.SALT);
    localStorage.removeItem(STORAGE_KEYS.HASH);
    localStorage.removeItem(STORAGE_KEYS.LAST_UNLOCKED);
    localStorage.removeItem(STORAGE_KEYS.FAILED_ATTEMPTS);
    localStorage.removeItem(STORAGE_KEYS.COOLDOWN_UNTIL);
    localStorage.removeItem(STORAGE_KEYS.BACKGROUNDED_AT);
    localStorage.removeItem(STORAGE_KEYS.PASSCODE_TYPE);
    console.log('[PIN] Cleared passcode localStorage keys');
    
    // 2. Clear ALL app-related localStorage keys (armsroom.* and others)
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('armsroom') || 
        key.startsWith('arms_room') ||
        key.startsWith('ArmsRoom')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('[PIN] Removed localStorage key:', key);
    });
    
    // 3. Clear all IndexedDB data using Dexie's proper table clearing
    // This is more reliable than deleting the entire database
    try {
      await wipeDatabase();
      console.log('[PIN] IndexedDB tables cleared via Dexie');
    } catch (dbError) {
      console.error('[PIN] Dexie wipe error, attempting raw IndexedDB delete:', dbError);
      
      // Fallback: Try raw IndexedDB deletion
      if (window.indexedDB) {
        try {
          const databases = ['ArmsRoomDB', 'arms_room_db'];
          for (const dbName of databases) {
            await new Promise((resolve) => {
              const deleteRequest = window.indexedDB.deleteDatabase(dbName);
              deleteRequest.onsuccess = () => {
                console.log(`[PIN] IndexedDB "${dbName}" deleted successfully`);
                resolve();
              };
              deleteRequest.onerror = () => resolve();
              deleteRequest.onblocked = () => resolve();
            });
          }
        } catch (rawDbError) {
          console.error('[PIN] Raw IndexedDB deletion error:', rawDbError);
        }
      }
    }
    
    // 4. Update React state
    setIsPinSet(false);
    setIsUnlocked(false);
    setFailedAttempts(0);
    setCooldownUntil(null);
    setPasscodeType('pin');
    
    console.log('[PIN] Full app data wipe completed');
    return true;
  }, []);

  // Reset PIN - calls wipeAllAppData (legacy wrapper for backward compatibility)
  const resetPin = useCallback(async () => {
    await wipeAllAppData();
  }, [wipeAllAppData]);

  const value = {
    isUnlocked,
    isPinSet,
    isLoading,
    failedAttempts,
    cooldownUntil,
    passcodeType,
    unlock,
    setupPin,
    setupPasscode,
    changePin,
    changePasscode,
    lock,
    resetPin,
    wipeAllAppData,
    clearCooldown,
  };

  return (
    <PinContext.Provider value={value}>
      {children}
    </PinContext.Provider>
  );
};
